/** Quote type value for Additional Works quotes (create/edit routing). */
export const ADDITIONAL_WORKS_QUOTE_TYPE = "Additional Works";
/**
 * Case-insensitive comparison for quote status.
 * Handles API returning "INPROGRESS" when frontend expects "inprogress".
 */
export function matchesQuoteStatus(quoteStatus, expected) {
    return (quoteStatus?.toLowerCase() || "") === expected;
}
/**
 * Compares defitType flexibly - handles both "full"/"FULL_DEFIT" and "partial"/"PARTIAL_DEFIT".
 */
export function matchesDefitType(quoteDefitType, expected) {
    const normalized = quoteDefitType?.toLowerCase().replace(/_defit$/, "") || "";
    return normalized === expected;
}
/**
 * Normalizes status for display lookups (getStatusLabel, getStatusClass).
 * Handles both "INPROGRESS" and "inprogress" from API.
 */
export function getQuoteStatusForDisplay(status) {
    const normalized = status?.toLowerCase() || "draft";
    const validStatuses = [
        "draft",
        "inprogress",
        "pending",
        "approved",
        "declined",
        "cancelled",
    ];
    return validStatuses.includes(normalized)
        ? normalized
        : "draft";
}
/**
 * Returns the contact id that owns or is assigned to the quote conversation.
 * Use this as the assigned contact when calling getThreadByQuote or sendMessage for quote threads.
 */
export function getQuoteAssignedContactId(quote) {
    return quote.attentionContactId ?? quote.contactId;
}
/**
 * Builds a map from section identifier to description from Cost Settings.
 * Key: sectionId or sectionId|sectionTitle (for joinery where multiple sections share same id).
 */
export function buildSectionDescriptionMap(costGroups) {
    const map = new Map();
    for (const group of costGroups) {
        for (const section of group.sections) {
            if (!section.description?.trim())
                continue;
            const key = section.title?.trim()
                ? `${section.id}|${section.title}`
                : section.id;
            map.set(key, section.description.trim());
        }
    }
    return map;
}
function getHeaderTitle(item) {
    if (item.category === "other") {
        // Manual / "other" entries: show the text the user typed (notes, then the
        // entered description) rather than the generic section label.
        return (item.notes?.trim() ||
            item.itemTitle?.trim() ||
            item.sectionTitle ||
            item.parentTitle);
    }
    return item.parentTitle || item.sectionTitle;
}
/**
 * Extracts scope of works items from quote lineItems (in-scope only).
 * When costGroups provided, uses description from Cost Settings when available;
 * otherwise falls back to header name (parentTitle/sectionTitle).
 * When descriptionOnly is true and costGroups provided, only includes items that have a description.
 */
export function getScopeOfWorksItems(lineItems, costGroups, options) {
    if (!lineItems)
        return [];
    const descriptionOnly = options?.descriptionOnly ?? false;
    const inScopeItems = lineItems.filter((item) => item.inScope);
    const uniqueTitles = new Set();
    const descriptionMap = costGroups?.length ? buildSectionDescriptionMap(costGroups) : null;
    inScopeItems.forEach((item) => {
        let displayText;
        // Additional Work "Other": the user's typed Note takes top priority,
        // above the Cost Settings description and the generic section label.
        if (item.category === "other") {
            displayText = item.notes?.trim() || undefined;
        }
        if (!displayText && descriptionMap && item.sectionId) {
            const keyWithSectionTitle = item.sectionTitle?.trim()
                ? `${item.sectionId}|${item.sectionTitle}`
                : null;
            const keyWithParentTitle = item.parentTitle?.trim()
                ? `${item.sectionId}|${item.parentTitle}`
                : null;
            displayText =
                (keyWithSectionTitle && descriptionMap.get(keyWithSectionTitle)) ||
                    (keyWithParentTitle && descriptionMap.get(keyWithParentTitle)) ||
                    descriptionMap.get(item.sectionId);
        }
        if (!displayText && !descriptionOnly) {
            displayText = getHeaderTitle(item);
        }
        if (displayText)
            uniqueTitles.add(displayText);
    });
    return Array.from(uniqueTitles);
}
/**
 * Formats scope of works items as a bulleted list string.
 */
export function formatScopeOfWorksAsBullets(scopeItems) {
    if (scopeItems.length === 0)
        return "";
    return scopeItems.map((s) => `• ${s}`).join("\n");
}
